"use client";
import { usePathname, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * For using a browser query parameter as a react state variable
 **/

export function useQueryState({ key, defaultValue = undefined, shouldUpdateQuery = true, asNumber = false, pushToHistory = false, multiple = false }) {
    const searchParams = useSearchParams();
    const pathname = usePathname();
    const queryValue = multiple ? searchParams.getAll(key) : searchParams.get(key);

    const getInitialValue = useCallback(() => {
        if (multiple) {
            if (queryValue && Array.isArray(queryValue) && queryValue.length > 0) {
                return queryValue;
            }
            return defaultValue;
        }
        if (queryValue !== null && queryValue !== undefined && queryValue !== "") {
            if (asNumber) {
                const n = Number(queryValue);
                return Number.isNaN(n) ? defaultValue : n;
            }
            if (queryValue === "true") {
                return true;
            }
            if (queryValue === "false") {
                return false;
            }
            return queryValue;
        }
        return defaultValue;
    }, [multiple, queryValue, asNumber, defaultValue]);

    const [value, setValueLocal] = useState(getInitialValue());

    const [initialValue] = useState(value);
    const [initialized] = useState(true); // TODO remove this, we don't need it anymore

    const valuesEqual = useCallback(
        (a, b) => {
            if (multiple) {
                const arrA = Array.isArray(a) ? a : a == null ? [] : [a];
                const arrB = Array.isArray(b) ? b : b == null ? [] : [b];
                if (arrA.length !== arrB.length) return false;
                for (let i = 0; i < arrA.length; i++) {
                    if (arrA[i] !== arrB[i]) return false;
                }
                return true;
            }
            return a === b;
        },
        [multiple],
    );

    const setValue = useCallback(
        (v) => {
            if (shouldUpdateQuery && (v != null || queryValue != null) && !valuesEqual(v, queryValue)) {
                const url = new URL(window.location.href);
                // Remove existing params for this key first
                url.searchParams.delete(key);
                if (multiple) {
                    const arr = Array.isArray(v) ? v : v != null ? [v] : [];
                    for (const val of arr) {
                        if (val != null && val !== "") url.searchParams.append(key, String(val));
                    }
                } else {
                    if (v != null && v !== "") {
                        url.searchParams.set(key, String(v));
                    }
                }
                if (pushToHistory) {
                    window.history.pushState(null, "", url.toString());
                } else {
                    window.history.replaceState(null, "", url.toString());
                }
            }
            setValueLocal(v);
        },
        [key, multiple, pushToHistory, queryValue, shouldUpdateQuery, valuesEqual],
    );

    const isFirstQuerySync = useRef(true);
    const lastPathname = useRef(pathname);
    // searchParams.getAll() returns a new array every render; stringify for a stable dep
    const queryValueKey = multiple ? JSON.stringify(queryValue) : queryValue;

    useEffect(() => {
        const pathnameChanged = lastPathname.current !== pathname;
        lastPathname.current = pathname;

        if (isFirstQuerySync.current) {
            isFirstQuerySync.current = false;
            return; // don't clobber defaultValue with an absent query param on mount
        }
        if (pathnameChanged) {
            return; // a route change can drop unrelated query params; don't clobber state over that
        }
        if (!shouldUpdateQuery) {
            return; // seeded once, then locally authoritative (e.g. a form field)
        }
        setValueLocal(getInitialValue());
    }, [queryValueKey, shouldUpdateQuery, pathname]);

    const resetValue = useCallback(() => {
        setValue(initialValue);
    }, [initialValue, setValue]);

    return [value, setValue, resetValue, initialized];
}
